import { useCallback, useEffect, useRef } from 'react';

type UseInViewCallbackOptions = {
  /** Called on every intersection change with the current in-view state. */
  onChange: (inView: boolean) => void;
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
};

/**
 * Native `IntersectionObserver` wrapper for repeating in-view callbacks (e.g. an
 * infinite-scroll "load more" sentinel). Returns a stable callback ref to attach
 * to the observed element and guarantees the observer is disconnected on unmount.
 *
 * Replaces `react-intersection-observer`, whose pooled observer could retain
 * observed elements after unmount. No-ops when `IntersectionObserver` is
 * unavailable (e.g. jsdom in unit tests).
 */
export const useInViewCallback = <T extends Element = Element>({
  onChange,
  root = null,
  rootMargin,
  threshold,
}: UseInViewCallbackOptions) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Keep the latest callback without forcing the ref to re-attach each render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || typeof IntersectionObserver === 'undefined') {
        return;
      }

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => onChangeRef.current(entry.isIntersecting));
        },
        { root, rootMargin, threshold }
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [root, rootMargin, threshold]
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    []
  );

  return { ref };
};
